import type { NewsItem } from '../../data/goldNews';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

type Props = {
  item: NewsItem;
  lang: 'zh-HK' | 'zh-CN' | 'en';
};

export default function NewsCard({ item, lang }: Props) {
  const { t } = useTranslation();
  const block = item.lang[lang];
  const date = new Date(item.publishedAt);
  const readMoreLabel = t('news.readFullArticle', { defaultValue: lang === 'zh-HK' ? '閱讀全文' : lang === 'zh-CN' ? '阅读全文' : 'Read full article' });

  return (
    <article className="bg-white rounded-3xl border border-gray-200 shadow-sm overflow-hidden hover:shadow-lg transition-all duration-300">
      {item.image && (
        <Link to={`/news/${item.slug}`} className="block aspect-[16/9] overflow-hidden bg-gray-100">
          <img
            src={item.image}
            alt={block.title}
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover transition-transform duration-500 hover:scale-[1.02]"
          />
        </Link>
      )}

      <div className="p-6">
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <span className="inline-flex px-3 py-1 rounded-full text-xs font-semibold bg-yellow-50 text-yellow-700 border border-yellow-200">
            {t(`news.filter.${item.category === 'central-bank' ? 'centralBank' : item.category}`, { defaultValue: item.category })}
          </span>
          <span className="text-xs text-gray-500">
            {date.toLocaleDateString(t('news.dateLocale', { defaultValue: lang === 'en' ? 'en-US' : lang === 'zh-CN' ? 'zh-CN' : 'zh-HK' }))}
          </span>
        </div>

        <h3 className="mb-3 text-xl font-bold leading-snug text-gray-900">
          <Link to={`/news/${item.slug}`} className="transition hover:text-yellow-600">
            {block.title}
          </Link>
        </h3>

        <p className="text-sm text-gray-600 mb-4 leading-7">
          {block.summary}
        </p>

        <div className="flex flex-wrap items-center justify-between gap-3 mt-5">
          <div className="flex flex-wrap gap-2">
            {item.tags.map((tag) => (
              <span
                key={tag}
                className="inline-flex px-2.5 py-1 rounded-full text-xs bg-gray-100 text-gray-600"
              >
                #{tag}
              </span>
            ))}
          </div>

          <Link
            to={`/news/${item.slug}`}
            className="inline-flex items-center gap-2 rounded-xl bg-gray-950 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-yellow-500 hover:text-gray-950"
          >
            {readMoreLabel}
            <i className="ri-arrow-right-line" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </article>
  );
}
